"use client";

import { PassiveScroll } from "@faire/retailer-visitor-shared/components/Scroll/Scroll";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { useMergedRefs } from "@faire/web--source/ui/hooks/useMergedRefs";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

type DebouncedFunc = ReturnType<typeof debounceIfPossible>;

interface IProps {
  children?: React.ReactNode | undefined;
  /** Top value used when sticky (this value is the distance between the top of the page and the sticky content) */
  top: number;
  /** z-index for the Sticky container */
  zIndex?: number;
  /** disables stickness */
  disabled?: boolean;
  /** References to the element that will contain Sticky or an approximation. It is used to calculate whether Sticky is floating or not  */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** event triggered when Sticky toggles between floating and not floating
   * (not floating is the original state, stuck at the end of the container is still considered sticky)
   */
  onFloatChange?: (isFloating: boolean) => void;
  /* className passed to the Sticky container, necessary when using styled-components  */
  className?: string;
}

/**
 * Makes the children sticky (https://css-tricks.com/position-sticky-2/)
 * with the addition of a listener that notifies when the content is floating or not.
 */
export const Sticky = ({
  ref,
  ...props
}: IProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const {
    top,
    children,
    className,
    containerRef,
    disabled,
    onFloatChange,
    zIndex,
  } = props;

  const [isFloating, setIsFloating] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergedRefs(stickyRef, ref);

  // not using useCallback because we are using throttle
  // and useCallback requires an inlined function
  const handleScrollRef = useRef<DebouncedFunc | undefined>(undefined);

  useEffect(() => {
    handleScrollRef.current = buildScrollHandler(
      containerRef?.current,
      stickyRef.current,
      setIsFloating
    );
    return () => {
      if (handleScrollRef.current && "cancel" in handleScrollRef.current) {
        handleScrollRef.current.cancel();
      }
    };
  }, [containerRef, stickyRef]);

  useEffect(() => {
    onFloatChange?.(isFloating);
  }, [isFloating, onFloatChange]);

  return (
    <Container
      ref={mergedRef}
      $top={top}
      $zIndex={zIndex ?? 0}
      $disabled={disabled}
      className={className}
    >
      <PassiveScroll onScroll={() => handleScrollRef.current?.()} />
      {children}
    </Container>
  );
};

const buildScrollHandler = (
  container: HTMLElement | undefined | null,
  sticky: HTMLDivElement | undefined | null,
  onFloatChange?: (isFloating: boolean) => void
): DebouncedFunc => {
  return throttleIfPossible(() => {
    const containerTop = container?.offsetTop;
    const headerTop = sticky?.offsetTop;

    if (isNotUndefined(containerTop) && isNotUndefined(headerTop)) {
      const absDiff = Math.abs(headerTop - containerTop);
      const isFloating = absDiff > STICKY_DETECTION_OFFSET_THRESHOLD;
      onFloatChange?.(isFloating);
    }
  }, 50);
};

const STICKY_DETECTION_OFFSET_THRESHOLD = 2;

const Container = styled(Column)<{
  $top: number;
  $disabled?: boolean;
  $zIndex: number;
}>`
  position: ${({ $disabled }) => ($disabled ? "unset" : "sticky")};
  top: ${({ $top }) => $top}px;
  z-index: ${({ $zIndex }) => $zIndex};
`;
